<script setup>
import { TypeChart, TypeFormulaVersion } from "@/utilities/enum";
</script>

<template>
  <DefaultLayout class="!bg-slate-50">
    <v-row class="mx-3 my-0 font-inter">
      <v-col cols="12">
        <div class="flex justify-between items-center">
          <div>
            <div class="flex justify-start items-center gap-1">
              <h5
                class="text-[10px] text-black text-nowrap cursor-pointer font-semibold"
                @click="$router.push('/dashboard-esg')"
              >
                Home
              </h5>
              <i class="mdi mdi-chevron-right text-lg"></i>
              <span
                class="text-[10px] text-black font-semibold cursor-pointer"
                @click="$router.push('/dashboard-esg')"
              >
                ESG Dashboard
              </span>
              <i class="mdi mdi-chevron-right text-lg"></i>
              <span class="text-[10px] text-black font-semibold">
                Bahan Bakar, Energi & Emisi
              </span>
              <i class="mdi mdi-chevron-right text-lg"></i>
              <h2 class="text-[10px] text-red-600 text-nowrap font-semibold">
                {{ chartActive.title }}
              </h2>
            </div>
            <v-menu transition="slide-x-transition">
              <template v-slot:activator="{ props }">
                <div class="flex gap-2 items-center max-w-fit" v-bind="props">
                  <h2 class="font-bold text-xl text-[#232323]">
                    {{ chartActive.title }}
                  </h2>
                  <i class="mdi mdi-menu-down text-2xl p-0 m-0"></i>
                </div>
              </template>
              <v-list class="h-fit">
                <v-list-item
                  v-for="item in chartOption.filter((data) =>
                    version === TypeFormulaVersion.V2 ? data.id !== 4 : true
                  )"
                  :key="item.id"
                  class="font-medium text-xs px-2 -my-2 text-gray-800 cursor-pointer hover:bg-gray-100"
                  @click="changeChartActive(item)"
                >
                  {{ item.title }}
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
          <div class="flex gap-2">
            <v-autocomplete
              variant="outlined"
              color="#475569"
              :items="years"
              v-model="year"
              item-title="title"
              item-value="value"
              hide-details
              density="compact"
              placeholder="Filter berdasarkan tahun"
              @update:model-value="loadPage"
              class="w-44"
              @input="getYear($event.target.value)"
              v-if="isAdmin"
            >
            </v-autocomplete>

            <v-btn
              variant="outlined"
              class="capitalize text-xs w-28 border-green-500"
              height="40"
            >
              <template #prepend>
                <img
                  src="../../assets/icon/download-green.png"
                  alt="Download Icon"
                  class="h-4 w-auto"
                />
              </template>
              <span class="text-green-500 font-medium font-inter">
                Export
              </span>
            </v-btn>
          </div>
        </div>
        <v-radio-group
          inline
          class="my-3"
          style="margin: 0 -0.5rem"
          hide-details
          density="compact"
          v-model="area"
          @update:model-value="filterArea"
        >
          <v-radio
            v-for="item in areas"
            :key="item.value"
            :label="item.title"
            :value="item.value"
            color="#dc2626"
            class="small-radio font-semibold"
          ></v-radio>
        </v-radio-group>

        <div class="w-full" v-if="version === TypeFormulaVersion.V1">
          <CardChartComponent
            v-if="chartActive.id === 1"
            :height="300"
            :icon="chart.fuel.icon"
            :bg-color-icon="chart.fuel.color"
            :is-large="true"
            :title="chart.fuel.title"
            :type="chart.fuel.type"
            :data="chart.fuel.data"
            :compare-data="compareData"
            :initial-data="initialData"
            :legend="chart.fuel.legend"
            :is-admin="isAdmin"
            :loading="loading"
            :id-ratio-legend="6"
            :get-chart="getChart"
            :get-compare-chart="getCompareChart"
          />
          <CardChartComponent
            v-if="chartActive.id === 2"
            :height="300"
            :icon="chart.energy.icon"
            :bg-color-icon="chart.energy.color"
            :is-large="true"
            :title="chart.energy.title"
            :type="chart.energy.type"
            :data="chart.energy.data"
            :initial-data="initialData"
            :legend="chart.energy.legend"
            :loading="loading"
            :id-ratio-legend="6"
            :get-chart="getChart"
          />
          <CardChartComponent
            v-if="chartActive.id === 3"
            :height="300"
            :icon="chart.emissionTon.icon"
            :bg-color-icon="chart.emissionTon.color"
            :is-large="true"
            :title="chart.emissionTon.title"
            :type="chart.emissionTon.type"
            :data="chart.emissionTon.data"
            :initial-data="initialData"
            :legend="chart.emissionTon.legend"
            :loading="loading"
            :id-ratio-legend="6"
            :get-chart="getChart"
          />
          <CardChartComponent
            v-if="chartActive.id === 4"
            :height="300"
            :icon="chart.emissionTonCo2e.icon"
            :bg-color-icon="chart.emissionTonCo2e.color"
            :is-large="true"
            :title="chart.emissionTonCo2e.title"
            :type="chart.emissionTonCo2e.type"
            :data="chart.emissionTonCo2e.data"
            :initial-data="initialData"
            :legend="chart.emissionTonCo2e.legend"
            :loading="loading"
            :id-ratio-legend="6"
            :get-chart="getChart"
          />
        </div>
        <div class="w-full" v-else-if="version === TypeFormulaVersion.V2">
          <CardChartV2Component
            v-if="chartActive.id === 1"
            :height="height"
            :icon="chart.fuel.icon"
            :bg-color-icon="chart.fuel.color"
            :is-large="true"
            :title="chart.fuel.title"
            :type="TypeChart.FUEL"
            :data="chart.fuel.data"
            :compare-data="compareData"
            :initial-data="initialData"
            :legend="chart.fuel.legend"
            :is-admin="isAdmin"
            :loading="loading"
            :id-ratio-legend="6"
            :get-chart="getChart"
            :get-compare-chart="getCompareChart"
          />
          <CardChartV2Component
            v-if="chartActive.id === 2"
            :height="height"
            :icon="chart.energy.icon"
            :bg-color-icon="chart.energy.color"
            :is-large="true"
            :title="chart.energy.title"
            :type="TypeChart.ENERGY"
            :data="chart.energy.data"
            :initial-data="initialData"
            :legend="chart.energy.legend"
            :loading="loading"
            :id-ratio-legend="7"
            :get-chart="getChart"
          />
          <CardChartV2Component
            v-if="chartActive.id === 3"
            :height="height"
            :icon="chart.emissionTon.icon"
            :bg-color-icon="chart.emissionTon.color"
            :is-large="true"
            :title="chart.emissionTon.title"
            :type="TypeChart.EMISSION_TON"
            :data="chart.emissionTon.data"
            :initial-data="initialData"
            :legend="chart.emissionTon.legend"
            :loading="loading"
            :id-ratio-legend="7"
            :get-chart="getChart"
          />
        </div>
      </v-col>
    </v-row>
  </DefaultLayout>
</template>

<script>
import DefaultLayout from "@/layouts/DefaultLayout.vue";
import CardChartComponent from "./components/CardChartComponent.vue";
import CardChartV2Component from "./components/CardChartV2Component.vue";
import { APP_BACKEND_ESG } from "@/constants";
import GlobalDataMixin from "@/GlobalDataMixin";
import axios from "axios";
import util from "@/assets/js/util";

const base_url = APP_BACKEND_ESG;

export default {
  name: "DetailDashboardEsgPage",
  mixins: [GlobalDataMixin],
  components: { DefaultLayout, CardChartComponent, CardChartV2Component },
  data() {
    return {
      isAdmin: false,
      workArea: null,
      kontraktorID: null,
      chartOption: [
        { id: 1, title: "Fuel Monitoring", type: "FUEL" },
        { id: 2, title: "Konsumsi Energi Monitoring", type: "ENERGY" },
        { id: 3, title: "Beban Emisi Monitoring (Ton)", type: "EMISSION TON" },
        {
          id: 4,
          title: "Total Beban Emisi ((TonCO2-e))",
          type: "EMISSION TON CO2-E",
        },
      ],
      areas: [
        { value: "", title: "All Area" },
        { value: "Hauling", title: "Hauling" },
        { value: "Mining", title: "Mining" },
        { value: "Project", title: "Project" },
        { value: "Port", title: "Port" },
        { value: "ROM", title: "ROM" },
        { value: "Others", title: "Others" },
      ],
      years: [],
      year: "",
      area: "",
      chartActive: {},
      chart: {
        fuel: {
          title: "Total Konsumsi Bahan Bakar",
          icon: "fuel-inactive.png",
          color: "bg-red-100",
          type: "FUEL",
          data: [],
          legend: [
            {
              id: 0,
              title: "Informasi Legenda",
              show: true,
            },
            {
              id: 0,
              title: "Informasi Legenda",
              show: true,
            },
            {
              id: 1,
              title: "Produksi (Ribu Ton)",
              show: true,
            },
            {
              id: 2,
              title: "Sumber Emisi Bergerak (RIbu Liter)",
              show: true,
            },
            {
              id: 3,
              title: "Sumber Emisi Tidak Bergerak (RIbu Liter)",
              show: true,
            },
            {
              id: 6,
              title: "Fuel Ratio (Liter Ton)",
              show: true,
            },
          ],
        },
        energy: {
          title: "Total Konsumsi Energi (MJ)",
          icon: "elektrifikasi-inactive.png",
          color: "bg-green-100",
          type: "ENERGY",
          data: [],
          legend: [
            {
              id: 0,
              title: "Informasi Legenda",
              show: true,
            },
            {
              id: 1,
              title: "Produksi (Ribu Ton)",
              show: true,
            },
            {
              id: 2,
              title: "Sumber Emisi Bergerak (Ribu Liter)",
              show: true,
            },
            {
              id: 3,
              title: "Sumber Emisi Tidak Bergerak (Ribu Liter)",
              show: true,
            },
            {
              id: 5,
              title: "Energi Terbarukan (x10^6 MJ)",
              show: true,
            },
            {
              id: 6,
              title: "Energi Elektrifikasi (x10^6 MJ)",
              show: true,
            },
            {
              id: 4,
              title: "Konsumsi Energi Fuel (x10^6 MJ)",
              show: true,
            },
            {
              id: 7,
              title: "Energi Ratio (MJ/Ton)",
              show: true,
            },
          ],
        },
        emissionTon: {
          title: "Total Beban Emisi (Ton)",
          icon: "carbon-dioxide-orange.png",
          color: "bg-orange-100",
          type: "EMISSION TON",
          data: [],
          legend: [
            {
              id: 0,
              title: "Informasi Legenda",
              show: true,
            },
            {
              id: 1,
              title: "Produksi (Ribu Ton)",
              show: true,
            },
            {
              id: 2,
              title: "Sumber Emisi Bergerak (Ribu Liter)",
              show: true,
            },
            {
              id: 3,
              title: "Sumber Emisi Tidak Bergerak (Ribu Liter)",
              show: true,
            },
            {
              id: 5,
              title: "Emisi Energi Terbarukan (Ribu TonCO2e)",
              show: true,
            },
            {
              id: 6,
              title: "Emisi Elektrifikasi (Ribu TonCO2e)",
              show: true,
            },
            {
              id: 8,
              title: "Emisi Fugitif (Ribu TonCO2e)",
              show: true,
            },
            {
              id: 4,
              title: "Emisi Fuel (Ribu TonCO2e)",
              show: true,
            },
            {
              id: 7,
              title: "Beban Emisi (Ribu Ton)",
              show: true,
            },
          ],
        },
        emissionTonCo2e: {
          title: "Total Beban Emisi ((TonCO2-e))",
          icon: "carbon-dioxide-orange.png",
          color: "bg-orange-100",
          type: "EMISSION TON CO2-E",
          data: [],
          legend: [
            {
              id: 0,
              title: "Informasi Legenda",
              show: true,
            },
            {
              id: 1,
              title: "Produksi (Ribu Ton)",
              show: true,
            },
            {
              id: 2,
              title: "Sumber Emisi Bergerak (RIbu Liter)",
              show: true,
            },
            {
              id: 3,
              title: "Sumber Emisi Tidak Bergerak (RIbu Liter)",
              show: true,
            },
            {
              id: 6,
              title: "Beban Emisi Ratio (x10-3 Ton/Ton)",
              show: true,
            },
            {
              id: 7,
              title: "Beban Emisi (Ribu Ton)",
              show: true,
            },
          ],
        },
      },
      initialData: [],
      compareData: [],
      loading: false,
      version: null,
      height: 300,
    };
  },
  async mounted() {
    let type = this.$router.currentRoute.value.params?.type;
    let area = this.$router.currentRoute.value.params?.workArea;

    this.area = area === "All" ? "" : area;
    if (type) {
      this.isAdmin =
        this.userData.role.includes("ADMIN") ||
        this.userData.role.includes("ENVIRONMENT") ||
        this.userData.role.includes("IMPROVEMENT");
      this.chartActive = this.chartOption.find((item) => item.type == type);

      await this.getYear();

      if (this.isAdmin) {
        this.getGroupByInitials();
      } else {
        this.workArea = this.userData.workArea;
        this.kontraktorID = parseInt(this.userData.kontraktorID);

        const area = this.userData.workArea
          .replace("[", "")
          .replace("]", "")
          .split(",")
          .map((item) => item.trim());

        this.areas = [{ title: "All Area", value: "" }];

        this.areas.push(
          ...area.map((data) => ({
            title: data,
            value: data,
          }))
        );
      }
      await this.getSetting();
      this.getChart(type, true);
    } else {
      this.$router.push("/dashboard-esg");
    }
  },
  methods: {
    loadPage() {
      let type = this.$router.currentRoute.value.params?.type;

      this.getChart(type, true);
      this.getCompareChart([]);
    },
    changeChartActive(chart) {
      this.chartActive = chart;
      let area = this.$router.currentRoute.value.params?.workArea;
      this.$router.push(`/dashboard-esg/${chart.type}/${area}`);
      this.getChart(chart.type, true);
    },
    filterArea() {
      let area = this.$router.currentRoute.value.params?.workArea;
      area = this.area === "" ? "All" : this.area;
      this.$router.push(`/dashboard-esg/${this.chartActive.type}/${area}`);
      this.getChart(this.chartActive.type, true);
    },
    getAdjustFactor(maxTotal) {
      const ranges = [
        { limit: 1e4, factor: 0.02 },
        { limit: 1e5, factor: 0.002 },
        { limit: 1e6, factor: 0.0002 },
        { limit: 1e7, factor: 0.00002 },
        { limit: 1e8, factor: 0.000002 },
        { limit: 1e9, factor: 0.0000002 },
        { limit: 1e10, factor: 0.00000002 },
        { limit: 1e11, factor: 0.000000002 },
        { limit: 1e12, factor: 0.0000000002 },
      ];

      for (const { limit, factor } of ranges) {
        if (maxTotal < limit) {
          return factor * maxTotal;
        }
      }

      // default kalau lebih besar dari semua limit
      return 0.00000000002 * maxTotal;
    },
    distributeRowHeightsAll(arrayData) {
      const MAX_HEIGHT = 224;
      const MIN_HEIGHT = 15;
      const EXCLUDE_KEYS = ["id", "type", "title", "dot_blue"];

      const totals = arrayData.map((row) => {
        const keys = Object.keys(row).filter((k) => !EXCLUDE_KEYS.includes(k));
        return keys.reduce((sum, k) => sum + row[k], 0);
      });

      const maxTotal = Math.max(...totals);
      const FACTOR = this.getAdjustFactor(maxTotal);

      const maxRatio = Math.max(...arrayData.map((d) => d.dot_blue));

      return arrayData.map((row, i) => {
        const keys = Object.keys(row).filter((k) => !EXCLUDE_KEYS.includes(k));
        const totalValue = totals[i];

        if (totalValue === 0) {
          const pxValues = {};
          keys.forEach((k) => (pxValues[`${k}_px`] = 0));
          return { ...row, ...pxValues };
        }

        const scaledMax = (totalValue / maxTotal) * MAX_HEIGHT;

        let pxValues = {};
        keys.forEach((k) => {
          let px = (row[k] / totalValue) * scaledMax;
          if (row[k] > 0 && px < MIN_HEIGHT) px = MIN_HEIGHT; // min jika > 0
          if (k === "fuel_useage") {
            px += FACTOR;
          }
          pxValues[`${k}_px`] = parseFloat(px.toFixed(4));
        });

        let totalPx = Object.values(pxValues).reduce((a, b) => a + b, 0);
        let excess = totalPx - MAX_HEIGHT;

        if (excess > 0) {
          let adjustableKeys = Object.keys(pxValues).filter(
            (k) => pxValues[k] > MIN_HEIGHT
          );
          while (excess > 0 && adjustableKeys.length > 0) {
            let reducePerKey = excess / adjustableKeys.length;
            adjustableKeys.forEach((k) => {
              if (pxValues[k] > MIN_HEIGHT) {
                let newValue = pxValues[k] - reducePerKey;
                if (newValue < MIN_HEIGHT) {
                  excess -= pxValues[k] - MIN_HEIGHT;
                  pxValues[k] = MIN_HEIGHT;
                } else {
                  pxValues[k] = parseFloat(newValue.toFixed(4));
                  excess -= reducePerKey;
                }
              }
            });
            adjustableKeys = Object.keys(pxValues).filter(
              (k) => pxValues[k] > MIN_HEIGHT
            );
          }
        }

        // posisi dot_blue
        let dotBluePos = 0;
        if (maxRatio > 0) {
          if (row.dot_blue === 0) {
            dotBluePos = 0;
          } else {
            dotBluePos = (row.dot_blue / maxRatio) * MAX_HEIGHT;

            const OFFSET_TOP_PERCENT = 0.3;
            const OFFSET_BOTTOM_PERCENT = 0.05;
            const OFFSET_TOP_PX = MAX_HEIGHT * OFFSET_TOP_PERCENT;
            const OFFSET_BOTTOM_PX = MAX_HEIGHT * OFFSET_BOTTOM_PERCENT;
            const TOLERANCE = 0.95;

            if (row.dot_blue === maxRatio) {
              dotBluePos = MAX_HEIGHT - OFFSET_TOP_PX;
            }

            if (dotBluePos < OFFSET_BOTTOM_PX) {
              dotBluePos = OFFSET_BOTTOM_PX;
            }

            if (row.dot_blue >= maxRatio * TOLERANCE) {
              dotBluePos = Math.min(dotBluePos, MAX_HEIGHT - OFFSET_TOP_PX - 5);
            }
          }
        }

        return { ...row, ...pxValues, dot_blue_px: dotBluePos };
      });
    },
    async getChart(type, is_quarterly) {
      this.loading = true;
      try {
        let params = {
          source: "cms",
          type: type,
          is_quarterly: is_quarterly,
          id_year: this.year,
          area: this.area,
        };
        if (this.isAdmin) {
          params.id_contractor = this.contractor;
        } else {
          params.id_contractor = this.kontraktorID;
        }

        const response = await axios.get(`${base_url}/dashboard/chart`, {
          params: params,
        });

        const result = response.data.data;

        if (result) {
          if (type === TypeChart.FUEL) {
            const arrayData = result.map((data, index) => ({
              id: index,
              title: data.title,
              production: data.production,
              val_amber: data.emission_bergerak,
              val_red: data.emission_tidak_bergerak,
              dot_blue: data.fuel_ratio,
            }));

            this.chart.fuel.data = this.distributeRowHeightsAll(arrayData);
          }
          if (type === TypeChart.ENERGY) {
            let arrayData = [];

            if (this.version === TypeFormulaVersion.V1) {
              arrayData = result.map((data, index) => ({
                id: index,
                title: data.title,
                production: data.production,
                val_amber: data.emission_bergerak,
                val_red: data.emission_tidak_bergerak,
                val_orange: data.consume_energy,
                val_emerald: data.energy_ratio,
                val_yellow: data.electrification,
                dot_blue: data.renewable_energy,
              }));
            } else if (this.version === TypeFormulaVersion.V2) {
              arrayData = result.map((data, index) => ({
                id: index,
                title: data.title,
                production: data.production,
                val_amber: data.emission_bergerak,
                val_red: data.emission_tidak_bergerak,
                val_orange: data.consume_energy,
                val_emerald: data.electrification,
                val_green: data.renewable_energy,
                dot_blue: data.energy_ratio,
              }));
            }
            this.chart.energy.data = this.distributeRowHeightsAll(arrayData);
          }
          if (type === TypeChart.EMISSION_TON) {
            let arrayData = [];
            if (this.version === TypeFormulaVersion.V1) {
              this.chart.emissionTon.data = result.map((data, index) => ({
                id: index,
                title: data.title,
                production: data.production,
                val_amber: data.emission_bergerak,
                val_red: data.emission_tidak_bergerak,
                val_yellow: data.beban_emission_ratio,
                dot_blue: data.beban_emission,
              }));
            } else if (this.version === TypeFormulaVersion.V2) {
              arrayData = result.map((data, index) => ({
                id: index,
                title: data.title,
                production: data.production,
                val_amber: data.emission_bergerak,
                val_red: data.emission_tidak_bergerak,
                val_orange: data.emission_fuel,
                val_green: data.emission_renewable,
                val_emerald: data.emission_electrification,
                val_lime: data.emission_fugitif,
                dot_blue: data.emission_ratio,
              }));
            }

            this.chart.emissionTon.data =
              this.distributeRowHeightsAll(arrayData);
          }
          if (type === "EMISSION TON CO2-E") {
            this.chart.emissionTonCo2e.data = result.map((data, index) => ({
              id: index,
              title: data.title,
              production: data.production,
              val_amber: data.emission_bergerak,
              val_red: data.emission_tidak_bergerak,
              val_yellow: data.beban_emission_ratio,
              dot_blue: data.beban_emission,
            }));
          }
        }
      } catch (err) {
        util.showError(err);
      } finally {
        setTimeout(() => {
          this.loading = false;
        }, 500);
      }
    },
    async getGroupByInitials() {
      try {
        let params = {
          source: "cms",
          id_year: this.year,
        };

        const response = await axios.get(
          `${base_url}/dashboard/group-by-initials`,
          {
            params: params,
          }
        );

        const result = response.data.data;

        if (result) {
          this.initialData = [
            { title: "Semua Mitra", value: 0 },
            ...result.map((data) => ({
              title: data.initials,
              value: data.id_contractor,
            })),
          ];
        }
      } catch (err) {
        util.showError(err);
      }
    },
    async getCompareChart(data) {
      try {
        const id_contractor = data.filter((item) => item !== 0);

        let params = {
          source: "cms",
        };
        params.id_contractor = id_contractor.length
          ? JSON.stringify(id_contractor)
          : null;

        params.id_year = this.year;

        const response = await axios.get(
          `${base_url}/dashboard/compare-chart`,
          {
            params: params,
          }
        );

        const result = response.data.data;

        if (result) {
          this.compareData = result;
        }
      } catch (err) {
        util.showError(err);
      }
    },
    async getYear(keyword = null) {
      try {
        const year = await axios.get(`${base_url}/year`, {
          params: {
            name: keyword?.trim(),
            size: -1,
          },
        });
        this.years = year.data.data.map((item) => ({
          title: item.name,
          value: item.id,
        }));
        this.year = year.data.data.find(
          (item) => item.name == new Date().getFullYear()
        )?.id;
      } catch (err) {
        util.showError(err);
      }
    },
    async getSetting() {
      try {
        const response = await axios.get(`${base_url}/sys-setting`);

        this.version = response.data.data.formula_version;
      } catch (err) {
        util.showError(err);
      }
    },
  },
};
</script>

<style scoped>
.small-radio {
  transform: scale(0.8);
}
</style>
